import React from 'react';
import { motion } from 'framer-motion';
import { createSegmentPath, createArcPath, svgDegreesToRadians, SABBATS_NORTHERN } from './clockUtils';
import { getCihPhaseColor, getSeasonColor } from '../utils/styleCard';
import { useTheme } from '../contexts/ThemeContext';

// Seasons aligned to horizon orientation (SVG coordinate system):
// In SVG: 0° = right, 90° = bottom, 180° = left, 270° = top
// 
// Our dial orientation (viewed like a compass/horizon):
// - EAST = left (180° SVG) = Spring starts here
// - NORTH = top (270° SVG) = Summer peaks here  
// - WEST = right (0°/360° SVG) = Autumn starts here
// - SOUTH = bottom (90° SVG) = Winter peaks here
//
// Season quadrants (90° each, counter-clockwise from East):
// Spring: 180° → 270° (East to North, upper-LEFT - Solar Dominant)
// Summer: 270° → 360° (North to West, upper-RIGHT - Solar Dominant)  
// Autumn: 0° → 90° (West to South, lower-RIGHT - Lunar Dominant)
// Winter: 90° → 180° (South to East, lower-LEFT - Lunar Dominant)
//
// CiH Phase Mapping:
// Winter = NOURISH (blue) - inward, restorative
// Spring = ACTIVATE (green) - outward, initiating
// Summer = FULFILL (orange) - expressive, manifesting
// Autumn = INTEGRATE (purple) - processing, releasing

// Northern Hemisphere temperate seasons (the standard wheel)
// Season positions are fixed - regional differences shown via zodiac/hands
const SEASONS_STANDARD = [
  { name: 'Spring', startAngle: 180, endAngle: 270 },
  { name: 'Summer', startAngle: 270, endAngle: 360 },
  { name: 'Autumn', startAngle: 0, endAngle: 90 },
  { name: 'Winter', startAngle: 90, endAngle: 180 }
];

// Equatorial - ITCZ-driven double wet/dry seasons mapped to CiH phases
// The wet/dry cycle maps to the four phases:
// - WET Primary (Mar-May): ACTIVATE - growth, monsoon brings activation energy
// - DRY Primary (Jun-Aug): FULFILL - peak dry, harvest/expression
// - WET Secondary (Sep-Nov): INTEGRATE - second rains, transition
// - DRY Secondary (Dec-Feb): NOURISH - rest period, conservation
//
// Angles aligned with temperate/polar for visual consistency:
// - NOURISH/DRY²: 90-180° (bottom-left)
// - ACTIVATE/WET¹: 180-270° (top-left)
// - FULFILL/DRY¹: 270-360° (top-right)
// - INTEGRATE/WET²: 0-90° (bottom-right)
const SEASONS_EQUATORIAL_QUAD = [
  { 
    name: 'Wet (Primary)',  // Full key matches interpretations directly
    displayName: 'WET¹', 
    startAngle: 180, 
    endAngle: 270,
    cihPhase: 'ACTIVATE'
  },
  { 
    name: 'Dry (Primary)',  // Full key matches interpretations directly
    displayName: 'DRY¹', 
    startAngle: 270, 
    endAngle: 360,
    cihPhase: 'FULFILL'
  },
  { 
    name: 'Wet (Secondary)',  // Full key matches interpretations directly
    displayName: 'WET²', 
    startAngle: 0, 
    endAngle: 90,
    cihPhase: 'INTEGRATE'
  },
  { 
    name: 'Dry (Secondary)',  // Full key matches interpretations directly
    displayName: 'DRY²', 
    startAngle: 90, 
    endAngle: 180,
    cihPhase: 'NOURISH'
  }
];

// Simple 2-segment for regions with single pronounced season (like Australasian)
// Maps to alternating phase pairs - uses generic "Wet" and "Dry" interpretations
const SEASONS_EQUATORIAL_SIMPLE = [
  { 
    name: 'Wet',  // Uses generic "Wet" interpretation
    displayName: 'WET', 
    startAngle: 180, 
    endAngle: 360,
    cihPhase: 'ACTIVATE'  // Wet = growth/activation
  },
  { 
    name: 'Dry',  // Uses generic "Dry" interpretation
    displayName: 'DRY', 
    startAngle: 0, 
    endAngle: 180,
    cihPhase: 'INTEGRATE'  // Dry = consolidation/integration
  }
];

// Polar regions - Four-quadrant mapping with interpretable season names
// The polar light cycle maps directly to the four CiH phases:
// - NOURISH: Polar Night (deep darkness, rest, inner journey)
// - ACTIVATE: Light Returns (twilight emerging, spring surge)
// - FULFILL: Polar Day (midnight sun, peak expression)
// - INTEGRATE: Light Fades (twilight fading, release)
//
// Angles match temperate seasons for visual consistency:
// - NOURISH/Winter: 90-180° (bottom-left, South-East)
// - ACTIVATE/Spring: 180-270° (top-left, East-North)
// - FULFILL/Summer: 270-360° (top-right, North-West)
// - INTEGRATE/Autumn: 0-90° (bottom-right, West-South)
const SEASONS_POLAR = [
  { 
    name: 'Light Returns',  // This key must match interpretations
    displayName: 'LIGHT RETURNS',
    polarState: 'Polar Twilight (Emerging) → Polar Day (Emerging)',
    startAngle: 180, 
    endAngle: 270,
    cihPhase: 'ACTIVATE'
  },
  { 
    name: 'Polar Day',  // This key must match interpretations
    displayName: 'POLAR DAY',
    polarState: 'Polar Day (Rising) → Polar Day (Peak) → Polar Day (Fading)',
    startAngle: 270, 
    endAngle: 360,
    cihPhase: 'FULFILL'
  },
  { 
    name: 'Light Fades',  // This key must match interpretations
    displayName: 'LIGHT FADES',
    polarState: 'Polar Twilight (Fading) → Polar Night (Approaching)',
    startAngle: 0, 
    endAngle: 90,
    cihPhase: 'INTEGRATE'
  },
  { 
    name: 'Polar Night',  // This key must match interpretations
    displayName: 'POLAR NIGHT',
    polarState: 'Polar Night (Deep)',
    startAngle: 90, 
    endAngle: 180,
    cihPhase: 'NOURISH'
  }
];

// Extended colors for equatorial qualifiers - these will be overridden by styleCard
const EQUATORIAL_COLORS = {
  'Wet': '#4682B4',      // Steel blue
  'Dry': '#DAA520',      // Goldenrod
  'WET¹': '#4169E1',     // Royal blue (primary wet)
  'DRY¹': '#CD853F',     // Peru (primary dry)
  'WET²': '#5F9EA0',     // Cadet blue (secondary wet)
  'DRY²': '#D2691E'      // Chocolate (secondary dry)
};

const SeasonsRing = ({ center, radius, season, onSeasonClick, locationIntelligence, isEquatorialZone = false }) => {
  const { theme } = useTheme();
  const isHighContrast = theme === 'high-contrast';
  
  const ringWidth = 28;
  const innerRadius = radius - ringWidth;
  const textRadius = radius - ringWidth / 2;
  const pathIdPrefix = `season-path-${center}`;
  
  // Determine which season configuration to use based on location intelligence
  let seasons;
  const climateZone = locationIntelligence?.climate_zone || season.type || 'temperate';
  const hemisphere = locationIntelligence?.hemisphere || 'north';
  const wetDryZone = season.wet_dry_zone || locationIntelligence?.wet_dry_zone;
  const isSouthern = hemisphere === 'south' || locationIntelligence?.is_southern;
  
  // Rotation offset logic:
  // - Polar zones: NO rotation - season ring represents universal light cycle (light at top)
  //   The zodiac/planetary hands invert for southern polar, but seasons ring stays fixed
  // - Equatorial zones: NO rotation - align with sabbats/light cycle
  // - Temperate zones: NO rotation - handled by ZodiacRing/PlanetaryHands
  const isEquatorial = climateZone === 'equatorial' || isEquatorialZone;
  const isPolar = climateZone === 'polar';
  const rotationOffset = 0;  // No rotation for any zone - alignment is universal
  
  if (climateZone === 'polar') {
    seasons = SEASONS_POLAR;
  } else if (climateZone === 'equatorial' || isEquatorialZone) {
    // Use 4-segment for most equatorial, but simple for australasian/pacific
    if (wetDryZone === 'australasian' || wetDryZone === 'pacific') {
      seasons = SEASONS_EQUATORIAL_SIMPLE;
    } else {
      seasons = SEASONS_EQUATORIAL_QUAD;
    }
  } else {
    // Temperate - always use standard season wheel
    // Regional differences are shown via zodiac ring and planetary hand rotation
    seasons = SEASONS_STANDARD;
  }
  
  // For equatorial, determine current season with qualifier
  const currentQualifier = season.qualifier;
  
  // For polar, determine current CiH phase from the API data
  // The polar ring uses CiH phase names, so we match against the dominant phase
  const currentPolarPhase = season.dominant_phase || season.cih_phase;

  // Helper to calculate text position in the middle of a segment arc
  // This ensures text is always readable from OUTSIDE the circle (like a clock face)
  const getTextPosition = (startAngle, endAngle, offset = 0) => {
    // Calculate the midpoint angle of the segment (with offset applied)
    const adjustedStart = (startAngle + offset) % 360;
    const adjustedEnd = (endAngle + offset) % 360;
    let midAngle = adjustedStart + ((adjustedEnd - adjustedStart + 360) % 360) / 2;
    if (adjustedEnd < adjustedStart) {
      midAngle = (adjustedStart + adjustedEnd + 360) / 2;
    }
    midAngle = midAngle % 360;
    
    const radians = midAngle * (Math.PI / 180);
    const x = center + textRadius * Math.cos(radians);
    const y = center + textRadius * Math.sin(radians);
    
    // Calculate rotation to keep text tangent to the arc and readable from outside
    // Base rotation: perpendicular to radius (tangent direction)
    let textRotation = midAngle + 90;
    
    // Normalize midAngle to 0-360 range for flip detection
    const normalizedMid = ((midAngle % 360) + 360) % 360;
    
    // In SVG coordinates:
    // 0° = RIGHT (3 o'clock), 90° = BOTTOM (6 o'clock)
    // 180° = LEFT (9 o'clock), 270° = TOP (12 o'clock)
    //
    // For text to be readable from OUTSIDE the circle:
    // - BOTTOM half (0° < angle < 180°): text needs to be flipped 180°
    //   This includes: RIGHT (0°), lower-right, BOTTOM (90°), lower-left, approaching LEFT
    // - TOP half (180° < angle < 360°): text is naturally readable
    //   This includes: LEFT (180°), upper-left, TOP (270°), upper-right, approaching RIGHT
    //
    // The boundary cases (0° and 180°) can go either way, but we'll include 0° in flip
    // to avoid the text being sideways at the exact right/left positions
    if (normalizedMid >= 0 && normalizedMid < 180) {
      textRotation += 180;
    }
    
    return { x, y, rotation: textRotation, midAngle };
  };

  return (
    <motion.g initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1, duration: 0.5 }} data-testid="seasons-ring">
      <circle cx={center} cy={center} r={radius} fill="none" stroke="#C5A059" strokeWidth="2" opacity="0.5" />
      <circle cx={center} cy={center} r={innerRadius} fill="none" stroke="#C5A059" strokeWidth="1" opacity="0.3" />

      {seasons.map((s, index) => {
        // Apply rotation offset to segments for equatorial/regional differentiation
        const adjustedStartAngle = (s.startAngle + rotationOffset) % 360;
        const adjustedEndAngle = (s.endAngle + rotationOffset) % 360;
        
        const { path, divisionLine } = createSegmentPath(center, innerRadius, radius, adjustedStartAngle, adjustedEndAngle);
        
        // Calculate text position (using rotated angles so text appears in correct segment)
        const textPos = getTextPosition(s.startAngle, s.endAngle, rotationOffset);
        
        // Determine if this is the current season segment
        let isCurrentSeason;
        if (isPolar) {
          // For polar, match by CiH phase name (NOURISH, ACTIVATE, FULFILL, INTEGRATE)
          isCurrentSeason = s.cihPhase === currentPolarPhase || s.name === currentPolarPhase;
        } else if (isEquatorial) {
          // For equatorial, match the full season name (e.g., "Wet (Primary)") 
          // or construct it from backend season + qualifier
          const backendFullName = season.qualifier 
            ? `${season.season} (${season.qualifier})`
            : season.season;
          isCurrentSeason = s.name === backendFullName || s.name === season.display_name;
        } else {
          // For temperate, match by season name
          isCurrentSeason = s.name === season.season;
        }
        
        // Get color - use CiH phase colors for polar AND equatorial
        let fillColor;
        if ((isPolar || isEquatorial) && s.cihPhase) {
          fillColor = getCihPhaseColor(s.cihPhase);
        } else {
          const displayKey = s.displayName || s.name;
          fillColor = EQUATORIAL_COLORS[displayKey] || getSeasonColor(s.name, isHighContrast);
        }
        
        // Get CiH phase color for accent
        const cihColor = getCihPhaseColor(s.cihPhase || 'NOURISH');
        
        // Display text - use displayName if available
        const displayText = s.displayName || s.name;

        return (
          <motion.g 
            key={`${s.name}-${s.qualifier || index}`} 
            style={{ cursor: 'pointer' }}
            whileHover={{ scale: 1.02 }}
            onClick={() => {
              console.log('Season clicked:', s.name, { isPolar, cihPhase: s.cihPhase });
              onSeasonClick?.('season', s.name, { 
                isEquatorial, 
                isPolar,
                cihPhase: s.cihPhase,
                polarState: s.polarState,
                displayName: s.displayName
              });
            }}
            data-testid={`season-section-${s.name.toLowerCase().replace(/[\s()]/g, '-')}`}
          >
            {/* Segment fill - USE CiH PHASE COLOR if available */}
            <motion.path 
              d={path} 
              fill={fillColor}
              opacity={isCurrentSeason ? 0.5 : 0.2}
              stroke={isCurrentSeason ? cihColor : fillColor} 
              strokeWidth={isCurrentSeason ? 2.5 : 1} 
              style={{ shapeRendering: 'geometricPrecision' }}
              whileHover={{ opacity: 0.6 }} 
            />
            <line {...divisionLine} stroke="#C5A059" strokeWidth="1" opacity="0.4" style={{ shapeRendering: 'crispEdges' }} />
            {/* CiH phase indicator for current season */}
            {isCurrentSeason && (
              <motion.path d={path} fill="none" stroke={cihColor} strokeWidth={3} opacity={0.6}
                animate={{ opacity: [0.4, 0.7, 0.4] }}
                transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} />
            )}
            {/* Season label - using positioned text for consistent readability */}
            <text 
              x={textPos.x} 
              y={textPos.y}
              fill={isCurrentSeason ? '#FFF' : fillColor} 
              fontSize={isEquatorial ? "14" : "16"} 
              fontWeight={isCurrentSeason ? 'bold' : 'normal'}
              fontFamily="serif" 
              textAnchor="middle"
              dominantBaseline="middle"
              transform={`rotate(${textPos.rotation}, ${textPos.x}, ${textPos.y})`}
              style={{ 
                pointerEvents: 'none', 
                textTransform: 'uppercase', 
                letterSpacing: isEquatorial ? '1px' : '2px',
                textShadow: isCurrentSeason ? `0 0 8px ${cihColor}` : 'none', 
                textRendering: 'optimizeLegibility' 
              }}
            >
              {displayText}
            </text>
          </motion.g>
        );
      })}
    </motion.g>
  );
};

export default SeasonsRing;
